/**
 * How long a secured search key from the my-account edge is valid. `SearchHandler` signs every key
 * with `ValidUntil = now + 1 hour`, and the response does not carry that expiry separately.
 */
export const SEARCH_KEY_LIFETIME_SECONDS = 60 * 60;

/** How far ahead of its expiry a key is replaced, so no search goes out on a key about to lapse. */
export const SEARCH_KEY_REFRESH_MARGIN_SECONDS = 5 * 60;

/**
 * Reads `validUntil` from an Algolia secured key: base64 of an HMAC followed by the restriction
 * query string it was signed with. Returns null for anything that does not decode to one.
 */
function readSignedValidUntil(token: string): number | null {
  try {
    const match = /validUntil=(\d+)/.exec(atob(token));
    return match ? Number(match[1]) : null;
  } catch {
    return null;
  }
}

/**
 * When a key received at `receivedAt` (unix seconds, local clock) stops working.
 *
 * The signed `validUntil` is in the edge's clock. A key lives an hour from signing whatever the
 * local clock says, so an hour from receipt caps it: a local clock running behind the edge cannot
 * then stretch a key past its real life. A shorter signed expiry is respected as is.
 */
export function getSearchKeyValidUntil(token: string, receivedAt: number): number {
  const byLifetime = receivedAt + SEARCH_KEY_LIFETIME_SECONDS;
  const signed = token ? readSignedValidUntil(token) : null;
  return signed === null ? byLifetime : Math.min(signed, byLifetime);
}

/** When a key that stops working at `validUntil` should be replaced. */
export function getSearchKeyRefreshAt(validUntil: number): number {
  return validUntil - SEARCH_KEY_REFRESH_MARGIN_SECONDS;
}
